import { capitalize } from 'lodash';
import { createColumnHelper } from '@tanstack/react-table';
import { VolumeX, ScanSearch } from 'lucide-react';

import { formatDate } from '@/portainer/filters/filters';

import { StatusBadge } from '@@/StatusBadge';
import { Button } from '@@/buttons';

import { AlertSeverityBadge } from '../../AlertSeverityBadge';

import { ActiveAlertRow } from './types';
import { statusToBadgeType } from './utils';
import { ExpandableMessage } from './ExpandableMessage';

export const columnHelper = createColumnHelper<ActiveAlertRow>();

/**
 * The scanning alert carries the workload labels needed to look up its
 * trivy-operator VulnerabilityReport, so only those rows get a details action.
 */
function hasVulnerabilityDetails(alert: ActiveAlertRow): boolean {
  // The modal looks the report up by environment, namespace and workload, so
  // without all three it can only render an empty state.
  return (
    alert.labels.alert_metric_name === 'trivy_image_vulnerabilities' &&
    Number.isFinite(Number(alert.labels.portainer_environment_id)) &&
    !!alert.labels.namespace &&
    !!alert.labels.resource_name
  );
}

export function createColumns(
  onSilence: (alert: ActiveAlertRow) => void,
  onViewDetails: (alert: ActiveAlertRow) => void
) {
  return [
    columnHelper.accessor('alertName', {
      header: 'Alert Name',
    }),
    columnHelper.accessor('environmentName', {
      header: 'Environment',
    }),
    columnHelper.accessor('environmentGroupName', {
      header: 'Group',
    }),
    columnHelper.display({
      id: 'actions',
      header: 'Actions',
      cell: ({ row: { original: alert } }) => (
        <div className="flex items-center gap-2">
          {hasVulnerabilityDetails(alert) && (
            <Button
              color="default"
              size="xsmall"
              icon={ScanSearch}
              onClick={() => onViewDetails(alert)}
              data-cy={`view-vulnerabilities-${alert.id}`}
              title="View vulnerability details"
            >
              Details
            </Button>
          )}
          <Button
            color="default"
            size="xsmall"
            icon={VolumeX}
            onClick={() => onSilence(alert)}
            data-cy={`silence-alert-${alert.id}`}
            title="Silence this alert"
          >
            Silence
          </Button>
        </div>
      ),
    }),
    columnHelper.accessor('state', {
      header: 'State',
      cell: ({ getValue }) => {
        const value = getValue();
        return (
          <StatusBadge color={statusToBadgeType[value]}>
            {capitalize(value)}
          </StatusBadge>
        );
      },
    }),
    columnHelper.accessor('severity', {
      header: 'Severity',
      cell: ({ getValue }) => {
        const value = getValue();
        return <AlertSeverityBadge severity={value} />;
      },
    }),
    columnHelper.accessor('message', {
      header: 'Message',
      cell: ({ getValue }) => (
        <ExpandableMessage>{getValue()}</ExpandableMessage>
      ),
      meta: { disableGrouping: true },
    }),
    columnHelper.accessor(({ startsAt }) => formatDate(startsAt), {
      header: 'Started At',
      id: 'startsAt',
      meta: { disableGrouping: true },
    }),
    columnHelper.accessor(({ updatedAt }) => formatDate(updatedAt), {
      header: 'Last Updated',
      id: 'updatedAt',
      meta: { disableGrouping: true },
    }),
  ];
}
